import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type BannerMessageType,
  type RawBannerMessage,
  useFetchOutageBannerQuery,
} from '~/api/easy-estimator-api/outage-banner-api/outageBannerService';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { getSessionData, setSessionData } from '~/core/helpers/storageHelper';

const APP_ID = 'EE3';
const DEFAULT_POLL_INTERVAL_S = 600;
const OUTAGE_POLL_DIVISOR = 2;
const MIN_POLL_INTERVAL_S = 60;

export type ActiveBannerMessage = {
  id: number;
  html: string;
  type: BannerMessageType;
};

const interpolateMessage = (raw: RawBannerMessage): string =>
  raw.message
    .replace(/\{eventStartTime\}/g, raw.eventStartTime)
    .replace(/\{eventEndTime\}/g, raw.eventEndTime);

const isWithinTimeWindow = (msg: RawBannerMessage): boolean => {
  const now = Date.now();

  return (
    now >= new Date(msg.messageStartTime).getTime() &&
    now <= new Date(msg.messageEndTime).getTime()
  );
};

const isRelevantApp = (msg: RawBannerMessage): boolean =>
  msg.applications.includes(APP_ID);

const filterAndPrioritize = (
  messages: RawBannerMessage[],
  dismissedIds: number[]
): RawBannerMessage | undefined => {
  const active = messages.filter(
    (msg) => isRelevantApp(msg) && isWithinTimeWindow(msg)
  );

  return (
    active.find((msg) => msg.type === 'outage') ??
    active.find(
      (msg) => msg.type === 'warning' && !dismissedIds.includes(msg.id)
    )
  );
};

const computePollMs = (serverInterval: number, hasOutage: boolean): number => {
  const base = serverInterval > 0 ? serverInterval : DEFAULT_POLL_INTERVAL_S;
  const seconds = hasOutage
    ? Math.max(base / OUTAGE_POLL_DIVISOR, MIN_POLL_INTERVAL_S)
    : base;

  return seconds * 1000;
};

const formatTime = (date: Date): string =>
  date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const useOutageBanner = () => {
  const [dismissedIds, setDismissedIds] = useState<number[]>(
    () => getSessionData<number[]>(LOCAL_STORAGE_KEY.OUTAGE_DISMISSED) ?? []
  );
  const [pollingInterval, setPollingInterval] = useState(
    DEFAULT_POLL_INTERVAL_S * 1000
  );

  const { data, fulfilledTimeStamp } = useFetchOutageBannerQuery(undefined, {
    pollingInterval,
  });

  const activeMessage: ActiveBannerMessage | null = useMemo(() => {
    const message = filterAndPrioritize(data?.messages ?? [], dismissedIds);

    return message
      ? {
          id: message.id,
          // TODO: sanitize HTML (e.g., DOMPurify) if message source becomes externally authored
          html: interpolateMessage(message),
          type: message.type,
        }
      : null;
  }, [data, dismissedIds]);

  const lastUpdated = fulfilledTimeStamp
    ? formatTime(new Date(fulfilledTimeStamp))
    : '';

  const dismissWarning = useCallback(() => {
    if (activeMessage?.type === 'warning') {
      const updated = [...dismissedIds, activeMessage.id];

      setSessionData(LOCAL_STORAGE_KEY.OUTAGE_DISMISSED, updated);
      setDismissedIds(updated);
    }
  }, [activeMessage, dismissedIds]);

  useEffect(() => {
    setPollingInterval(
      computePollMs(data?.pollInterval ?? 0, activeMessage?.type === 'outage')
    );
  }, [data?.pollInterval, activeMessage?.type]);

  return {
    activeMessage,
    dismissWarning,
    lastUpdated,
  };
};
